import { useProjects } from "@features/projects/hooks/useProjects";
import { WorkbenchSessionPill } from "@features/workbench/components/WorkbenchSessionPill";
import {
  COPILOT_RAIL_EDGE_WIDTH,
  COPILOT_RAIL_WIDTH,
} from "@layouts/AppLayout/constants";
import {
  type CopilotRailPlacement,
  useChatSurface,
} from "@layouts/AppLayout/useLayoutMode";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineRounded";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRightRounded";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRightRounded";
import OpenInFullIcon from "@mui/icons-material/OpenInFullRounded"; // used by CopilotRailEdge below
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useChatStore } from "@stores/chatStore";
import { useLayoutStore } from "@stores/layoutStore";
import { useOrgStore } from "@stores/orgStore";
import { Suspense, lazy, useState } from "react";
import { useSession } from "../hooks/useSession";
import { ChatConversation } from "./ChatConversation";

// SessionCreateDialog drags in MUI Dialog + Radio + TextField + Alert
// (~50 KB minified) — lazy-load so the eager workbench path stays
// lean. Loaded the first time the user clicks "New session". Replaces
// the old ToolPickerDialog (retired May 2026 with the tool picker).
const SessionCreateDialog = lazy(() =>
  import("@features/workbench/components/SessionCreateDialog").then((m) => ({
    default: m.SessionCreateDialog,
  })),
);

interface CopilotRailProps {
  projectId: string;
  /**
   * Layout placement: docked reserves space in the flex row (push-aside);
   * overlay floats above content so toggling never reflows the workbench.
   */
  placement?: CopilotRailPlacement;
}

/**
 * Persistent right-rail Copilot — the same conversation as `/chat`,
 * rendered alongside whatever page the user is on.
 *
 * Two placements:
 *   - "docked"  (default, desktop md+): the rail occupies a flex column
 *     and pushes content aside.
 *   - "overlay" (tablet sm): the rail floats above content with absolute
 *     positioning so opening it doesn't reflow the workbench. The 36px
 *     edge tab stays mounted in the flex row underneath, giving the user
 *     a stable affordance to re-open after collapsing.
 *
 * The rail and the `/chat` page never render simultaneously: AppLayout
 * hides this on chat surfaces. So the shared `chatStore` singleton is
 * driven by exactly one mounted `useChat` at a time.
 */
export function CopilotRail({
  projectId,
  placement = "docked",
}: CopilotRailProps) {
  const setCopilotRailPin = useLayoutStore((s) => s.setCopilotRailPin);
  const copilotRailExpanded = useLayoutStore((s) => s.copilotRailExpanded);
  const toggleCopilotRailExpanded = useLayoutStore(
    (s) => s.toggleCopilotRailExpanded,
  );
  const sessionId = useChatStore((s) => s.sessionId);
  const sessionTitle = useChatStore((s) => s.sessionTitle);
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  // Workbench-Tools (Phase 1): the rail's header pill + empty state +
  // Tool picker only render on the Workbench surface. Other surfaces
  // (Requirements, standalone /chat) keep the legacy "Copilot" eyebrow
  // + ChatConversation-driven empty state.
  const surface = useChatSurface();
  const isWorkbench = surface === "workbench";
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const projectsQuery = useProjects(activeOrgId);
  const project = projectsQuery.data?.find((p) => p.id === projectId);
  const boards = project?.boards ?? [];
  const { loadSessionById } = useSession();
  const [pickerOpen, setPickerOpen] = useState(false);

  // Phone-sized viewports can't host a 380px column without crowding
  // the page out — defer to phone-only chrome (drawer pattern) later.
  if (isPhone) return null;

  const handleCollapse = () => setCopilotRailPin("closed");

  const isOverlay = placement === "overlay";

  // Workbench session UX: pill replaces the "Copilot" eyebrow; the
  // empty-state CTA replaces ChatConversation when no session is bound
  // (so the user can't bypass the Tool picker by typing into the
  // composer — every workbench session begins with an explicit Tool
  // selection, locked product decision).
  const showWorkbenchEmptyState = isWorkbench && !sessionId;
  const handleSessionSelected = (sid: string) => {
    // No projectId — the user is already on this project's workbench
    // (or requirements). Passing projectId sets `overridden=true` in
    // useNavigateChat, which skips the "stay on workbench/requirements
    // shell" branch and forces a redirect to /projects/$id/chat. That
    // override path is meant for the sidebar auto-promoting a project
    // session into its project's chat shell — wrong fit when the rail
    // is already mounted in the project shell.
    loadSessionById(sid);
  };

  // sessionsQuery / loadout-change marker reads went away in May 2026
  // with the workbench tool picker. The rail no longer renders any
  // per-session loadout chips or tool change markers in the transcript.

  // When the user has clicked the "expand" toggle, the docked rail
  // widens to share the page 50/50 with the main content (replaces
  // the old "open in full chat" navigation away). Overlay mode keeps
  // a fixed width — there's nothing to push aside, expansion would
  // just blanket the workbench underneath.
  const expanded = copilotRailExpanded && !isOverlay;

  return (
    <Box
      role={isOverlay ? "dialog" : undefined}
      aria-label={isOverlay ? "Copilot conversation" : undefined}
      sx={(t) => ({
        // Expanded mode: take half the page (flex:1) so the rail
        // matches the main content area's width. Compact mode (the
        // default): fixed COPILOT_RAIL_WIDTH.
        width: expanded ? "auto" : COPILOT_RAIL_WIDTH,
        flex: isOverlay ? "none" : expanded ? "1 1 0" : "0 0 auto",
        minWidth: expanded ? COPILOT_RAIL_WIDTH : undefined,
        transition: isOverlay
          ? undefined
          : "width 200ms ease, flex-basis 200ms ease",
        borderLeft: `1px solid ${t.palette.border.default}`,
        bgcolor: t.palette.surface.default,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        position: isOverlay ? "absolute" : "relative",
        ...(isOverlay && {
          top: 0,
          right: 0,
          bottom: 0,
          height: "100%",
          // Sit above the workbench main + the edge tab; below the
          // app-level drawer (sidebar) which can portal over everything.
          zIndex: t.zIndex.drawer - 1,
          // Soft drop-shadow so the panel reads as floating above the
          // workbench, not a hard slab pasted onto it.
          boxShadow: "-12px 0 32px -16px rgba(0, 0, 0, 0.55)",
          // Slide-in from the right with the spring-feel curve.
          animation:
            "cf-copilot-slide-in 220ms cubic-bezier(0.34, 1.36, 0.64, 1) both",
          "@keyframes cf-copilot-slide-in": {
            from: { transform: "translateX(16px)", opacity: 0 },
            to: { transform: "translateX(0)", opacity: 1 },
          },
        }),
        // Subtle editorial-amber halo at the top, mirroring the mockup.
        // Pointer-events:none so the rail's own content is interactive.
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: `radial-gradient(420px 520px at 50% -10%, ${t.palette.editorial.softer}, transparent 60%)`,
          pointerEvents: "none",
        },
      })}
    >
      {/* Edge handle for expand/collapse. Sits on the left border of
          the rail, vertically centred. Compact: left-chevron (clicking
          grows the rail leftward). Expanded: right-chevron (clicking
          shrinks the rail rightward). Only rendered in docked mode —
          overlay floats above the workbench so resizing wouldn't
          change anything visible. */}
      {!isOverlay && (
        <Tooltip
          title={
            copilotRailExpanded
              ? "Shrink chat back to the rail"
              : "Expand chat to share the page with the workbench"
          }
          placement="left"
        >
          <IconButton
            size="small"
            onClick={toggleCopilotRailExpanded}
            aria-label={
              copilotRailExpanded ? "Shrink chat rail" : "Expand chat rail"
            }
            aria-pressed={copilotRailExpanded}
            sx={(t) => ({
              position: "absolute",
              top: "50%",
              left: 0,
              // Half the handle width past the rail's left border, so
              // the chevron sits centred ON the border line. The
              // pill-shape (rounded right side, flat left side) reads
              // as part of the rail rather than a floating button.
              transform: "translate(-50%, -50%)",
              width: 18,
              height: 36,
              borderRadius: "10px",
              p: 0,
              bgcolor: t.palette.surface.default,
              color: t.palette.text.secondary,
              border: `1px solid ${t.palette.border.default}`,
              boxShadow: `0 2px 6px ${alpha(t.palette.common.black, 0.18)}`,
              // Sit above the rail's content + ::before halo so the
              // hit-target isn't covered, but below app-level drawers.
              zIndex: 2,
              transition: `background-color ${t.motion.fast}, color ${t.motion.fast}, transform ${t.motion.fast}`,
              "&:hover": {
                bgcolor: t.palette.surface.raised,
                color: t.palette.text.primary,
              },
            })}
          >
            {copilotRailExpanded ? (
              <ChevronRightIcon sx={{ fontSize: 16 }} />
            ) : (
              <ChevronLeftIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      )}

      {/* Head — title row + actions. Slim; the conversation owns the
          vertical space. */}
      <Box
        sx={(t) => ({
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.25,
          borderBottom: `1px solid ${t.palette.border.default}`,
        })}
      >
        <Box
          sx={(t) => ({
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: t.palette.editorial.main,
            boxShadow: `0 0 8px ${t.palette.editorial.main}`,
            flexShrink: 0,
          })}
        />
        {isWorkbench ? (
          <WorkbenchSessionPill
            projectId={projectId}
            organisationId={activeOrgId}
            activeSessionId={sessionId}
            activeTitle={sessionTitle}
            onSessionSelect={handleSessionSelected}
            onNewSession={() => setPickerOpen(true)}
          />
        ) : (
          <Typography
            sx={{
              fontFamily: "var(--font-mono, monospace)",
              fontSize: "0.6875rem",
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "editorial.main",
            }}
          >
            Copilot
          </Typography>
        )}

        <Box sx={{ ml: "auto", display: "flex", gap: 0.25 }}>
          {isWorkbench && sessionId && (
            <Tooltip title="New session">
              <IconButton
                size="small"
                onClick={() => setPickerOpen(true)}
                aria-label="Start a new Workbench session"
                sx={{ p: "5px", color: "text.secondary" }}
              >
                <AddCircleOutlineIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Collapse Copilot">
            <IconButton
              size="small"
              onClick={handleCollapse}
              aria-label="Collapse Copilot rail"
              sx={{ p: "5px", color: "text.secondary" }}
            >
              <KeyboardDoubleArrowRightIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Conversation core — same hook → same store → same conversation
          as the /chat page. On the Workbench surface with no session
          bound, render the Tool-picker empty state instead so the user
          can't bypass the picker by typing into the composer (every
          workbench session begins with an explicit Tool selection). */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {showWorkbenchEmptyState ? (
          <WorkbenchRailEmptyState onStart={() => setPickerOpen(true)} />
        ) : (
          <ChatConversation variant="rail" />
        )}
      </Box>

      {isWorkbench && pickerOpen && (
        <Suspense fallback={null}>
          <SessionCreateDialog
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            projectId={projectId}
            boards={boards}
            organisationId={activeOrgId}
            onCreated={(session) => {
              // Mirror the new session's board scope into chatStore
              // up-front so the ChatScopeChip above the composer
              // reflects what the user just picked. The sessions-list
              // refetch (kicked off by useCreateSession's invalidate)
              // would catch up via the workbench page's sync effect,
              // but the synchronous write avoids the brief "All PCBs"
              // flicker before the trailing effect fires.
              useChatStore
                .getState()
                .setActiveChatBoardIds(session.board_ids ?? []);
              handleSessionSelected(session.session_id);
              setPickerOpen(false);
            }}
          />
        </Suspense>
      )}
    </Box>
  );
}

/**
 * Empty state for the Workbench rail when no session is bound. Single
 * CTA — "Start a session" — opens the Tool picker. Deliberately
 * compact: the rail's vertical real estate is precious and a wall of
 * onboarding copy here would feel like a paywall.
 */
function WorkbenchRailEmptyState({ onStart }: { onStart: () => void }) {
  return (
    <Box
      sx={(t) => ({
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        px: 3,
        py: 4,
        textAlign: "center",
        color: t.palette.text.secondary,
      })}
    >
      <Box
        sx={(t) => ({
          width: 44,
          height: 44,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: alpha(t.palette.primary.main, 0.1),
          color: t.palette.primary.main,
          mb: 0.5,
        })}
      >
        <AddCircleOutlineIcon sx={{ fontSize: 22 }} />
      </Box>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, color: "text.primary" }}
      >
        Start a Workbench session
      </Typography>
      <Typography variant="caption" sx={{ maxWidth: 240, lineHeight: 1.5 }}>
        Pick the analysis Tools you want active for this conversation — Power,
        Thermal, SPICE, DRC, Controller — and the agent will scope its reasoning
        to your selection.
      </Typography>
      <Button
        variant="contained"
        size="small"
        onClick={onStart}
        sx={{ mt: 1, px: 2 }}
      >
        Pick Tools
      </Button>
    </Box>
  );
}

/**
 * Edge tab shown in place of the rail when collapsed. Single click
 * re-opens. Lives at the same width-token slot so the page layout
 * doesn't reflow as the user toggles.
 */
export function CopilotRailEdge() {
  const setCopilotRailPin = useLayoutStore((s) => s.setCopilotRailPin);
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  if (isPhone) return null;

  return (
    <Box
      sx={(t) => ({
        width: COPILOT_RAIL_EDGE_WIDTH,
        flex: "0 0 auto",
        borderLeft: `1px solid ${t.palette.border.default}`,
        bgcolor: t.palette.surface.default,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        py: 1.25,
        gap: 1,
      })}
    >
      <Tooltip title="Open Copilot" placement="left">
        <IconButton
          size="small"
          onClick={() => setCopilotRailPin("open")}
          aria-label="Open Copilot rail"
          sx={(t) => ({
            color: "editorial.main",
            p: "5px",
            transition: `transform ${t.motion.fast}`,
            "&:active": { transform: "scale(0.92)" },
          })}
        >
          <OpenInFullIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
      <Box
        sx={(t) => ({
          width: 4,
          height: 36,
          borderRadius: 2,
          background: t.palette.editorial.main,
          opacity: 0.55,
        })}
      />
    </Box>
  );
}
